import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { toast } from 'sonner'
import { Eye, MoreHorizontal, Pencil, Plus, Send, Trash2, Undo2, Users } from 'lucide-react'

import { MarkdownContent } from '@/components/markdown-content'
import { RichTextEditor } from '@/components/rich-text-editor'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  adminGetAnnouncementReaders,
  adminListAnnouncements,
  createAnnouncement,
  deleteAnnouncement,
  publishAnnouncement,
  updateAnnouncement,
  withdrawAnnouncement,
  type AdminAnnouncement,
  type AnnouncementDraft,
} from '@/lib/announcements-api'
import { formatDateTime, fromInputDateTime, toInputDateTime } from '@/lib/date'
import { getErrorMessage } from '@/lib/error-messages'
import { markdownToPlainText } from '@/lib/markdown'
import { queryKeys } from '@/lib/query-keys'
import type { UserRole } from '@/types/domain'

const roleOptions: { value: UserRole; label: string }[] = [
  { value: 'member', label: 'Teilnehmer' },
  { value: 'trainer', label: 'Trainer' },
  { value: 'admin', label: 'Admins' },
]

const roleLabels: Record<UserRole, string> = {
  member: 'Teilnehmer',
  trainer: 'Trainer',
  admin: 'Admin',
}

const announcementSchema = z
  .object({
    title: z.string().trim().min(1, 'Titel ist erforderlich'),
    body: z.string().trim().min(1, 'Text ist erforderlich'),
    target_roles: z.array(z.enum(['member', 'trainer', 'admin'])).min(1, 'Mindestens eine Zielgruppe wählen'),
    visible_from: z.string(),
    visible_until: z.string(),
  })
  .refine(
    (values) =>
      !values.visible_from ||
      !values.visible_until ||
      new Date(values.visible_until) > new Date(values.visible_from),
    { message: 'Muss nach „Sichtbar ab“ liegen', path: ['visible_until'] },
  )

type AnnouncementFormValues = z.infer<typeof announcementSchema>

const emptyForm: AnnouncementFormValues = {
  title: '',
  body: '',
  target_roles: ['member', 'trainer', 'admin'],
  visible_from: '',
  visible_until: '',
}

type DisplayStatus = 'draft' | 'scheduled' | 'active' | 'expired' | 'withdrawn'

function getDisplayStatus(announcement: AdminAnnouncement, now = new Date()): DisplayStatus {
  if (announcement.status === 'draft') return 'draft'
  if (announcement.status === 'withdrawn') return 'withdrawn'
  if (announcement.visible_until && new Date(announcement.visible_until) <= now) return 'expired'
  if (announcement.visible_from && new Date(announcement.visible_from) > now) return 'scheduled'
  return 'active'
}

const statusBadges: Record<DisplayStatus, { label: string; className: string }> = {
  draft: { label: 'Entwurf', className: 'bg-muted text-muted-foreground' },
  scheduled: { label: 'Geplant', className: 'bg-amber-500/15 text-amber-600 dark:text-amber-400' },
  active: { label: 'Aktiv', className: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
  expired: { label: 'Abgelaufen', className: 'bg-muted text-muted-foreground' },
  withdrawn: { label: 'Zurückgezogen', className: 'bg-destructive/15 text-destructive' },
}

type PendingAction = { type: 'publish' | 'withdraw' | 'delete'; announcement: AdminAnnouncement }

export function AdminAnnouncementsPage() {
  const queryClient = useQueryClient()
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editing, setEditing] = useState<AdminAnnouncement | null>(null)
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null)
  const [readersFor, setReadersFor] = useState<AdminAnnouncement | null>(null)
  const [previewFor, setPreviewFor] = useState<AdminAnnouncement | null>(null)

  const announcementsQuery = useQuery({
    queryKey: queryKeys.adminAnnouncements,
    queryFn: adminListAnnouncements,
  })

  const form = useForm<AnnouncementFormValues>({
    resolver: zodResolver(announcementSchema),
    defaultValues: emptyForm,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.adminAnnouncements })

  const saveMutation = useMutation({
    mutationFn: (values: AnnouncementFormValues) => {
      const draft: AnnouncementDraft = {
        title: values.title.trim(),
        body: values.body.trim(),
        target_roles: values.target_roles,
        visible_from: fromInputDateTime(values.visible_from),
        visible_until: fromInputDateTime(values.visible_until),
      }
      return editing ? updateAnnouncement(editing.id, draft) : createAnnouncement(draft)
    },
    onSuccess: () => {
      invalidate()
      toast.success(editing ? 'Benachrichtigung aktualisiert.' : 'Entwurf erstellt.')
      setIsFormOpen(false)
      setEditing(null)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const actionMutation = useMutation({
    mutationFn: ({ type, announcement }: PendingAction) => {
      if (type === 'publish') return publishAnnouncement(announcement.id)
      if (type === 'withdraw') return withdrawAnnouncement(announcement.id)
      return deleteAnnouncement(announcement.id)
    },
    onSuccess: (_, { type }) => {
      invalidate()
      toast.success(
        type === 'publish'
          ? 'Benachrichtigung veröffentlicht.'
          : type === 'withdraw'
            ? 'Benachrichtigung zurückgezogen.'
            : 'Benachrichtigung gelöscht.',
      )
      setPendingAction(null)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const openCreate = () => {
    setEditing(null)
    form.reset(emptyForm)
    setIsFormOpen(true)
  }

  const openEdit = (announcement: AdminAnnouncement) => {
    setEditing(announcement)
    form.reset({
      title: announcement.title,
      body: announcement.body,
      target_roles: announcement.target_roles,
      visible_from: toInputDateTime(announcement.visible_from),
      visible_until: toInputDateTime(announcement.visible_until),
    })
    setIsFormOpen(true)
  }

  const announcements = announcementsQuery.data ?? []

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">In-App-Benachrichtigungen</h2>
          <p className="text-sm text-muted-foreground">
            Werden beim nächsten Aufruf der App als Dialog angezeigt und müssen bestätigt werden.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Neue Benachrichtigung
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {announcementsQuery.isLoading ? (
            <div className="space-y-2 p-6">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : announcementsQuery.isError ? (
            <div className="flex flex-col items-center gap-3 p-8 text-center">
              <p className="text-sm text-destructive">{getErrorMessage(announcementsQuery.error)}</p>
              <Button variant="outline" size="sm" onClick={() => announcementsQuery.refetch()}>
                Erneut laden
              </Button>
            </div>
          ) : announcements.length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">
              Noch keine Benachrichtigungen vorhanden.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Titel</TableHead>
                  <TableHead>Zielgruppe</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sichtbar ab</TableHead>
                  <TableHead>Sichtbar bis</TableHead>
                  <TableHead className="text-right">Gelesen</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {announcements.map((announcement) => {
                  const status = getDisplayStatus(announcement)
                  const badge = statusBadges[status]
                  const percent =
                    announcement.target_count > 0
                      ? Math.round((announcement.read_count / announcement.target_count) * 100)
                      : 0
                  const editable = announcement.status !== 'published'

                  return (
                    <TableRow key={announcement.id}>
                      <TableCell className="max-w-xs">
                        <button
                          type="button"
                          className="block w-full truncate text-left font-medium hover:underline"
                          onClick={() => setPreviewFor(announcement)}
                        >
                          {announcement.title}
                        </button>
                        <div className="truncate text-xs text-muted-foreground">
                          {markdownToPlainText(announcement.body)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {announcement.target_roles.map((role) => (
                            <Badge key={role} variant="outline">
                              {roleLabels[role]}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={badge.className}>
                          {badge.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        {announcement.visible_from
                          ? formatDateTime(announcement.visible_from)
                          : announcement.published_at && announcement.status === 'published'
                            ? formatDateTime(announcement.published_at)
                            : 'Bei Veröffentlichung'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        {announcement.visible_until ? formatDateTime(announcement.visible_until) : 'Unbegrenzt'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right text-sm">
                        <button
                          type="button"
                          className="tabular-nums hover:underline disabled:no-underline"
                          onClick={() => setReadersFor(announcement)}
                          disabled={announcement.read_count === 0}
                        >
                          {announcement.read_count} / {announcement.target_count}
                        </button>
                        <div className="text-xs text-muted-foreground">{percent}%</div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" aria-label="Aktionen">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => setPreviewFor(announcement)}>
                              <Eye className="mr-2 h-4 w-4" />
                              Anzeigen
                            </DropdownMenuItem>
                            {editable && (
                              <DropdownMenuItem onSelect={() => openEdit(announcement)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Bearbeiten
                              </DropdownMenuItem>
                            )}
                            {editable && (
                              <DropdownMenuItem
                                onSelect={() => setPendingAction({ type: 'publish', announcement })}
                              >
                                <Send className="mr-2 h-4 w-4" />
                                {announcement.status === 'withdrawn' ? 'Erneut veröffentlichen' : 'Veröffentlichen'}
                              </DropdownMenuItem>
                            )}
                            {announcement.status === 'published' && (
                              <DropdownMenuItem
                                onSelect={() => setPendingAction({ type: 'withdraw', announcement })}
                              >
                                <Undo2 className="mr-2 h-4 w-4" />
                                Zurückziehen
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onSelect={() => setReadersFor(announcement)}>
                              <Users className="mr-2 h-4 w-4" />
                              Leser anzeigen
                            </DropdownMenuItem>
                            {editable && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onSelect={() => setPendingAction({ type: 'delete', announcement })}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Löschen
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Benachrichtigung bearbeiten' : 'Neue Benachrichtigung'}</DialogTitle>
            <DialogDescription>
              Wird als Entwurf gespeichert und erst nach dem Veröffentlichen angezeigt.
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit((values) => saveMutation.mutate(values))} className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Titel</FormLabel>
                    <FormControl>
                      <Input placeholder="z. B. Neue Öffnungszeiten" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="body"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Text</FormLabel>
                    <FormControl>
                      <RichTextEditor value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="target_roles"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Zielgruppe</FormLabel>
                    <div className="flex flex-wrap gap-4">
                      {roleOptions.map((option) => (
                        <label key={option.value} className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={field.value.includes(option.value)}
                            onCheckedChange={(checked) =>
                              field.onChange(
                                checked === true
                                  ? [...field.value, option.value]
                                  : field.value.filter((role) => role !== option.value),
                              )
                            }
                          />
                          {option.label}
                        </label>
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="visible_from"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sichtbar ab (optional)</FormLabel>
                      <FormControl>
                        <Input type="datetime-local" {...field} />
                      </FormControl>
                      <FormDescription>Leer = sofort bei Veröffentlichung.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="visible_until"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sichtbar bis (optional)</FormLabel>
                      <FormControl>
                        <Input type="datetime-local" {...field} />
                      </FormControl>
                      <FormDescription>Leer = unbegrenzt.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setIsFormOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? 'Speichert...' : 'Entwurf speichern'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={pendingAction !== null} onOpenChange={(open) => !open && setPendingAction(null)}>
        <DialogContent>
          {pendingAction && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {pendingAction.type === 'publish'
                    ? 'Benachrichtigung veröffentlichen?'
                    : pendingAction.type === 'withdraw'
                      ? 'Benachrichtigung zurückziehen?'
                      : 'Benachrichtigung löschen?'}
                </DialogTitle>
                <DialogDescription>
                  {pendingAction.type === 'publish'
                    ? pendingAction.announcement.visible_from &&
                      new Date(pendingAction.announcement.visible_from) > new Date()
                      ? `„${pendingAction.announcement.title}“ wird ab ${formatDateTime(pendingAction.announcement.visible_from)} angezeigt.`
                      : `„${pendingAction.announcement.title}“ wird sofort allen Benutzern der Zielgruppe angezeigt.`
                    : pendingAction.type === 'withdraw'
                      ? `„${pendingAction.announcement.title}“ wird nicht mehr angezeigt. Bisherige Lesebestätigungen bleiben erhalten.`
                      : `„${pendingAction.announcement.title}“ wird inklusive Lesebestätigungen endgültig gelöscht.`}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setPendingAction(null)}>
                  Abbrechen
                </Button>
                <Button
                  variant={pendingAction.type === 'publish' ? 'default' : 'destructive'}
                  disabled={actionMutation.isPending}
                  onClick={() => actionMutation.mutate(pendingAction)}
                >
                  {pendingAction.type === 'publish'
                    ? 'Veröffentlichen'
                    : pendingAction.type === 'withdraw'
                      ? 'Zurückziehen'
                      : 'Löschen'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <PreviewDialog announcement={previewFor} onClose={() => setPreviewFor(null)} />
      <ReadersDialog announcement={readersFor} onClose={() => setReadersFor(null)} />
    </div>
  )
}

// Shows the announcement as members see it, without recording a read receipt.
function PreviewDialog({
  announcement,
  onClose,
}: {
  announcement: AdminAnnouncement | null
  onClose: () => void
}) {
  const badge = announcement ? statusBadges[getDisplayStatus(announcement)] : null

  return (
    <Dialog open={announcement !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        {announcement && badge && (
          <>
            <DialogHeader>
              <DialogTitle>{announcement.title}</DialogTitle>
              <DialogDescription asChild>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary" className={badge.className}>
                    {badge.label}
                  </Badge>
                  {announcement.published_at && (
                    <span>{`Veröffentlicht am ${formatDateTime(announcement.published_at)}`}</span>
                  )}
                </div>
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[60vh] overflow-y-auto">
              <MarkdownContent>{announcement.body}</MarkdownContent>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Schließen
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

function ReadersDialog({
  announcement,
  onClose,
}: {
  announcement: AdminAnnouncement | null
  onClose: () => void
}) {
  const readersQuery = useQuery({
    queryKey: queryKeys.announcementReaders(announcement?.id ?? ''),
    queryFn: () => adminGetAnnouncementReaders(announcement!.id),
    enabled: announcement !== null,
  })

  return (
    <Dialog open={announcement !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Gelesen von</DialogTitle>
          <DialogDescription>
            {announcement &&
              `„${announcement.title}“ – ${announcement.read_count} von ${announcement.target_count} Benutzern`}
          </DialogDescription>
        </DialogHeader>
        {readersQuery.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : readersQuery.isError ? (
          <p className="text-sm text-destructive">{getErrorMessage(readersQuery.error)}</p>
        ) : (readersQuery.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">Noch von niemandem gelesen.</p>
        ) : (
          <Table containerClassName="max-h-[60vh] overflow-y-auto">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>E-Mail</TableHead>
                <TableHead>Rolle</TableHead>
                <TableHead>Gelesen am</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {readersQuery.data?.map((reader) => (
                <TableRow key={reader.user_id}>
                  <TableCell>{reader.full_name || '-'}</TableCell>
                  <TableCell>{reader.email}</TableCell>
                  <TableCell>{roleLabels[reader.role] ?? reader.role}</TableCell>
                  <TableCell className="whitespace-nowrap">{formatDateTime(reader.read_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  )
}
